import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Frame, Header, Footer, Btn, Box } from '../components/Frame';
import { useStore } from '../lib/store';
import { callFn } from '../lib/firebase';
import { toBase64Url, fromBase64Url } from '../lib/crypto';
import {
  decryptMessage,
  markViewed,
  acceptChatRequest,
  sendEncryptedMessage,
  sendEncryptedAttachment,
  unwrapAttachmentKey,
  unwrapAttachmentKeySelf,
  loadOtherUser,
  pinPeerKey,
  computeSafetyCode,
  decryptSelfMessage,
  unsendMessage,
  deleteSelfCopy,
  subscribeMySignal,
} from '../lib/messaging';
import { sendFsMessage, decryptFsMessage, sendFsAttachment } from '../lib/fsMessaging';
import { buildLocationPayload, parseLocation, osmLink, getCurrentPositionOnce } from '../lib/location';
import AttachmentViewer from './AttachmentViewer';
import { getCachedChat, setCachedChat } from '../lib/sessionCache';

const POLL_MS = 2500;

// Allegati: Storage attivato (bucket nullum-prod.firebasestorage.app) → ON.
const ATTACHMENTS_ENABLED = true;

function fmtClock(ts) {
  if (!ts) return '';
  const date =
    typeof ts === 'number'
      ? new Date(ts)
      : typeof ts?.toDate === 'function'
      ? ts.toDate()
      : ts instanceof Date
      ? ts
      : null;
  if (!date) return '';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

const TTL_OPTIONS = [
  { value: 0, label: 'zero' },
  { value: 10, label: '10s' },
  { value: 300, label: '5m' },
  { value: 3600, label: '1h' },
  { value: 86400, label: '24h' },
  { value: -1, label: '∞' },  // premium-only, alert mostrato alla selezione
];

export default function ChatScreen() {
  const navigate = useNavigate();
  const { chatId } = useParams();
  const { uid: myUid, boxSec, boxPub, firebaseUser, derivedKeys, sigil: myMasterSigil } = useStore();
  const myFirebaseUid = firebaseUser?.uid || null;

  // I MIEI sigil (master + eventuali derivati premium), in minuscolo.
  const mySigilsLower = (() => {
    const s = new Set();
    if (myMasterSigil) s.add(String(myMasterSigil).toLowerCase());
    for (const k of Object.keys(derivedKeys || {})) s.add(String(k).toLowerCase());
    return s;
  })();

  // "È mio?" si decide per SIGIL (stabile), non per uid. Con l'auth a token custom
  // (Opzione 2) l'uid Firebase di un'identità cambia rispetto al vecchio anon: i
  // messaggi inviati col vecchio uid risulterebbero "ricevuti" se confrontati per
  // uid. Il sigil del mittente (senderSigilLower, scritto dal server) è invece
  // stabile. Fallback all'uid per i rarissimi messaggi legacy senza sigil.
  const isMine = (m) => {
    if (!m) return false;
    const sl = m.senderSigilLower ? String(m.senderSigilLower).toLowerCase() : null;
    if (sl && mySigilsLower.has(sl)) return true;
    const su = m.senderUid;
    return su != null && (su === myFirebaseUid || su === myUid);
  };

  const [chat, setChat] = useState(() => getCachedChat(myFirebaseUid, chatId)?.chat || null);
  const [chatErr, setChatErr] = useState(null);
  const [otherUser, setOtherUser] = useState(null);
  const [keyChanged, setKeyChanged] = useState(false); // TOFU: chiave cambiata?
  const [safetyCode, setSafetyCode] = useState('');
  const [showSafety, setShowSafety] = useState(false);
  const [viewer, setViewer] = useState(null);   // { msg, key } per AttachmentViewer
  const [attaching, setAttaching] = useState(false);
  const [attachErr, setAttachErr] = useState(null);
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const [attachDownloadable, setAttachDownloadable] = useState(false); // toggle: allegato salvabile
  const [pendingImg, setPendingImg] = useState(null); // { file, url } — anteprima immagine prima dell'invio
  const [pendingLoc, setPendingLoc] = useState(null); // { lat, lng, acc } — posizione catturata, in anteprima
  const [locPrecision, setLocPrecision] = useState('exact'); // 'exact' | 'approx' (scelta utente)
  const [locBusy, setLocBusy] = useState(false);
  // 4 input nascosti: galleria (foto/video), registra video, registra audio,
  // file qualsiasi. Su mobile l'attributo `capture` apre direttamente la
  // fotocamera o il microfono; su desktop ricade sul file picker.
  const inputGalleryRef = useRef(null);
  const inputVideoLiveRef = useRef(null);
  const inputAudioLiveRef = useRef(null);
  const inputAnyFileRef = useRef(null);

  // Tick di 1s per il countdown live di scomparsa sui messaggi letti.
  const [nowTs, setNowTs] = useState(Date.now());
  useEffect(() => {
    const i = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);

  // Conferme di lettura: preferenza di VISUALIZZAZIONE per-chat (default ON).
  // NB: il viewedAt e' comunque registrato (serve al TTL); questo decide solo
  // se TU vedi "letto ✓✓ + countdown" su questa chat.
  const [readReceipts, setReadReceipts] = useState(() => {
    try { return localStorage.getItem(`nullum.rr.${chatId}`) !== '0'; } catch { return true; }
  });
  function toggleReadReceipts() {
    const v = !readReceipts;
    try { localStorage.setItem(`nullum.rr.${chatId}`, v ? '1' : '0'); } catch {}
    setReadReceipts(v);
  }

  const [remoteMsgs, setRemoteMsgs] = useState(() => getCachedChat(myFirebaseUid, chatId)?.messages || []);
  const [decrypted, setDecrypted] = useState({});
  const [localOutgoing, setLocalOutgoing] = useState([]);
  const viewedRef = useRef(new Set());
  const fsDoneRef = useRef(new Set()); // msg FS già decifrati (decifratura una-tantum)
  const fsFileKeysRef = useRef({});    // msgId → chiave-file (b64) per gli allegati FS
  const isMountedRef = useRef(true);

  // Segna la chat come "vista" (per il badge "nuove" nell'inbox): all'apertura
  // e ad ogni arrivo di messaggi mentre sei dentro.
  useEffect(() => {
    try { localStorage.setItem(`nullum.chatSeen.${chatId}`, String(Date.now())); } catch {}
  }, [chatId, remoteMsgs]);

  const [replyText, setReplyText] = useState('');
  const [replyTtl, setReplyTtl] = useState(10);
  const [showTtl, setShowTtl] = useState(false); // durata messaggio: nascosta di default
  const [sending, setSending] = useState(false);
  const [replyErr, setReplyErr] = useState(null);

  const [accepting, setAccepting] = useState(false);
  const [acceptErr, setAcceptErr] = useState(null);

  const bottomRef = useRef(null);

  async function fetchOnce() {
    try {
      const fn = callFn('listChatMessages');
      const res = await fn({ chatId, nullumUid: myUid });
      if (!isMountedRef.current) return;
      const payload = res.data;
      if (payload?.ok === false) {
        console.warn('listChatMessages server error:', payload.stage, payload.error);
        setChatErr(`server: ${payload.error}${payload.stage ? ` (${payload.stage})` : ''}`);
      } else {
        setChat(payload?.chat || null);
        setRemoteMsgs(payload?.messages || []);
        setCachedChat(myFirebaseUid, chatId, { chat: payload?.chat || null, messages: payload?.messages || [] });
        setChatErr(null);
      }
    } catch (err) {
      if (!isMountedRef.current) return;
      console.warn('listChatMessages transport error', err);
      setChatErr(err?.message || 'errore di rete');
    }
  }

  useEffect(() => {
    if (!chatId || !myUid) return;
    isMountedRef.current = true;
    fetchOnce();
    // REAL-TIME al posto del polling a 2.5s: il server aggiorna
    // users/{uid}.rtSignalAt su nuovo messaggio / lettura (✓✓) / unsend → qui
    // rifetchiamo SOLO allora (anche in background, è raro). Poll lento 25s di
    // sicurezza, solo se la tab è visibile, nel caso un segnale si perda.
    let lastTick = 0;
    const off = subscribeMySignal(myFirebaseUid, (millis) => {
      if (!isMountedRef.current) return;
      if (millis && millis === lastTick) return; // dedup
      lastTick = millis;
      fetchOnce();
    });
    const slow = setInterval(() => {
      if (document.visibilityState === 'visible') fetchOnce();
    }, 25000);
    return () => {
      isMountedRef.current = false;
      try { off && off(); } catch {}
      clearInterval(slow);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, myUid, myFirebaseUid]);

  useEffect(() => {
    if (!chat || !myUid) return;

    // PREFERITO: profilo dell'altro risolto LATO SERVER (per @sigil → robusto al
    // MULTI-DEVICE). Bug storico: calcolando "l'altro" per uid, chi ha più
    // device propri pescava un PROPRIO device → chiavi sbagliate
    // ("[messaggio illeggibile]" / "chiavi non pronte"). Ci fidiamo di
    // chat.otherParticipant (listChatMessages lo risolve per sigil).
    const op = chat.otherParticipant;
    if (op && op.pubKeyBox) {
      if (otherUser?.uid === op.uid && otherUser?.pubKeyBox) return;
      setOtherUser({ uid: op.uid, sigil: op.sigil || '???', pubKeyBox: op.pubKeyBox });
      setKeyChanged(pinPeerKey(op.uid, op.pubKeyBox) === 'changed');
      return;
    }

    // FALLBACK LEGACY: chat vecchie senza otherParticipant dal server. Calcola
    // l'altro per uid (escludendo i nostri id) e legge diretto.
    const otherUid = (chat.participants || []).find(
      (p) => p !== myFirebaseUid && p !== myUid,
    );
    if (!otherUid) return;
    if (otherUser?.uid === otherUid && otherUser?.pubKeyBox) return;
    const sigilFromMap = chat.sigils?.[otherUid];
    let cancelled = false;
    loadOtherUser(otherUid)
      .then((profile) => {
        if (cancelled) return;
        setOtherUser({
          uid: otherUid,
          sigil: sigilFromMap || profile?.sigil || '???',
          pubKeyBox: profile?.pubKeyBox || null,
        });
        if (profile?.pubKeyBox) {
          setKeyChanged(pinPeerKey(otherUid, profile.pubKeyBox) === 'changed');
        }
      })
      .catch((err) => {
        console.warn('loadOtherUser failed', err);
        if (cancelled) return;
        setOtherUser({ uid: otherUid, sigil: sigilFromMap || '???', pubKeyBox: null });
      });
    return () => { cancelled = true; };
  }, [chat, myUid, myFirebaseUid, otherUser]);

  // Codice di sicurezza verificabile (anti-MITM, punto 2): deterministico
  // dalle due chiavi box, identico per entrambi gli utenti.
  useEffect(() => {
    if (!boxPub || !otherUser?.pubKeyBox) { setSafetyCode(''); return; }
    let cancelled = false;
    (async () => {
      try {
        const myB64 = await toBase64Url(boxPub);
        const code = await computeSafetyCode(myB64, otherUser.pubKeyBox);
        if (!cancelled) setSafetyCode(code);
      } catch { /* non bloccante */ }
    })();
    return () => { cancelled = true; };
  }, [boxPub, otherUser]);

  useEffect(() => {
    if (!myUid || !boxSec || !otherUser?.pubKeyBox) return;
    // Finche' la chat e' "pending for me" (richiesta non ancora accettata)
    // NON decifriamo i messaggi in arrivo: il "Accetta e leggi" deve essere
    // reale, non una formalita' sopra un contenuto gia' in chiaro.
    const pendingForMe = (chat?.status === 'pending' || chat?.pending === true) &&
      !!(chat?.initiator || chat?.requestedBy) &&
      (chat?.initiator ?? chat?.requestedBy) !== myFirebaseUid &&
      (chat?.initiator ?? chat?.requestedBy) !== myUid;
    let cancelled = false;
    (async () => {
      const updates = {};
      for (const m of remoteMsgs) {
        if (decrypted[m.id] !== undefined) continue;
        if (isMine(m)) {
          // miei messaggi: provo a rileggere la copia-per-sé (se presente)
          if (m.selfCipher) {
            const self = await decryptSelfMessage(m, boxPub, boxSec);
            if (self != null) {
              // Allegato FS: la copia-per-sé è un JSON {k:chiave-file, c:caption}.
              if (Number(m.v) === 2 && m.hasAttachment) {
                try { const j = JSON.parse(self); fsFileKeysRef.current[m.id] = j.k; updates[m.id] = j.c ?? ''; }
                catch { updates[m.id] = self; }
              } else {
                updates[m.id] = self;
              }
            }
          }
          continue;
        }
        if (pendingForMe) continue; // bloccati finche' non accetto

        // ── Canale forward-secret (wire v2 = ratchet) ───────────────────────
        // Decifratura UNA-TANTUM (il ratchet consuma le chiavi) e retry-safe:
        // se la sessione non è ancora pronta (es. il msg di setup non è ancora
        // arrivato) NON segniamo "illeggibile", riproviamo al prossimo giro.
        if (Number(m.v) === 2) {
          if (fsDoneRef.current.has(m.id)) continue;
          let fsText = null;
          try {
            const ctx = getMySigilContextForChat();
            const peerSigil = otherUser?.sigil || chat?.otherSigilLower || null;
            fsText = await decryptFsMessage({
              boxKeypair: { pub: ctx.pubKey, priv: ctx.secKey },
              msg: m,
              peerSigil,
            });
          } catch {
            continue; // sessione non pronta / errore: riprova dopo
          }
          fsDoneRef.current.add(m.id);
          // Allegato FS: il testo decifrato è un JSON {k:chiave-file, c:caption}.
          if (m.hasAttachment) {
            try { const j = JSON.parse(fsText); fsFileKeysRef.current[m.id] = j.k; updates[m.id] = j.c ?? ''; }
            catch { updates[m.id] = fsText; }
          } else {
            updates[m.id] = fsText;
          }
          continue;
        }

        // ── Canale legacy (box statico) ─────────────────────────────────────
        // Try master key first, poi iterate sulle chiavi derivate. Il msg
        // potrebbe essere stato cifrato verso il nostro sigil derivato
        // (es. qualcuno ha scritto a @vento_libero), quindi serve la
        // privKey derivata corrispondente.
        let text = null;
        try {
          text = await decryptMessage(m, boxSec, otherUser.pubKeyBox);
        } catch {}
        if (text == null && derivedKeys) {
          for (const k of Object.values(derivedKeys)) {
            try {
              text = await decryptMessage(m, k.boxSec, otherUser.pubKeyBox);
              if (text != null) break;
            } catch {}
          }
        }
        updates[m.id] = (text != null) ? text : '[messaggio illeggibile]';
      }
      if (cancelled || Object.keys(updates).length === 0) return;
      setDecrypted((cur) => ({ ...cur, ...updates }));
    })();
    return () => {
      cancelled = true;
    };
  }, [remoteMsgs, otherUser, myUid, myFirebaseUid, boxSec, boxPub, decrypted, chat, derivedKeys]);

  useEffect(() => {
    if (!chatId || !myUid) return;
    const isAccepted =
      chat && chat.status !== 'pending' && chat.pending !== true;
    if (!isAccepted) return;
    for (const m of remoteMsgs) {
      if (isMine(m)) continue;
      if (m.viewedAt) continue;
      // gli allegati partono col TTL solo quando aperti (one-view), non
      // all'apertura della chat: li marca AttachmentViewer.
      if (m.hasAttachment) continue;
      if (viewedRef.current.has(m.id)) continue;
      viewedRef.current.add(m.id);
      markViewed(chatId, m.id).catch((err) => {
        console.warn('markViewed failed for', m.id, err);
        viewedRef.current.delete(m.id);
      });
    }
  }, [remoteMsgs, chatId, myUid, chat]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [remoteMsgs, localOutgoing]);

  useEffect(() => {
    if (localOutgoing.length === 0) return;
    setLocalOutgoing((cur) =>
      cur.filter(
        (local) =>
          !remoteMsgs.some(
            (r) =>
              isMine(r) &&
              r.createdAt &&
              Math.abs(r.createdAt - local.createdAt) < 60_000
          )
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteMsgs, myUid]);

  const renderedMsgs = useMemo(() => {
    // Se la chat e' "pending for me", non mostro affatto i messaggi in
    // arrivo: l'utente vede solo il banner "richiesta + Accetta e leggi".
    // I miei messaggi (se ho gia' iniziato io una conversazione che e'
    // poi diventata pending dall'altra parte) continuo a mostrarli.
    const pendingForMe = (chat?.status === 'pending' || chat?.pending === true) &&
      !!(chat?.initiator || chat?.requestedBy) &&
      (chat?.initiator ?? chat?.requestedBy) !== myFirebaseUid &&
      (chat?.initiator ?? chat?.requestedBy) !== myUid;
    // Scadenza ANCORATA al primo viewedAt (immutabile): il messaggio sparisce
    // dalla vista a primaLettura + TTL, lato client, anche se la cancellazione
    // server e' in ritardo. Rileggere NON riavvia il timer.
    const isExpired = (m) => {
      const v = m.viewedAt; const ttl = m.senderTTL;
      if (!v || ttl == null || ttl < 0) return false; // non letto o ∞ → resta
      if (ttl === 0) return nowTs >= v;                 // zero-persist
      return nowTs >= v + ttl * 1000;
    };
    const theirs = pendingForMe ? [] : remoteMsgs
      .filter((m) => !isMine(m) && !isExpired(m))
      .map((m) => ({
        key: m.id,
        mine: false,
        text: decrypted[m.id] ?? '...',
        loc: parseLocation(decrypted[m.id]),
        createdAt: m.createdAt ?? Date.now(),
        ttl: m.senderTTL,
        viewedAt: m.viewedAt,
        att: m.hasAttachment ? m : null, // messaggio allegato in arrivo
      }));
    const mineRemote = remoteMsgs
      .filter((m) => isMine(m) && !isExpired(m))
      .map((m) => {
        const selfTxt = decrypted[m.id]; // copia-per-sé decifrata (se attiva)
        const selfRemoved = !!m.selfDeletedAt || (m.selfCipher == null && !m.hasAttachment);
        let text;
        if (selfRemoved) {
          text = '— rimosso dalla mia chat —';
        } else if (m.hasAttachment) {
          const label = m.kind === 'video' ? '🎬 video inviato'
            : m.kind === 'audio' ? '🎙 audio inviato'
            : m.kind === 'file' ? `📎 file inviato: ${m.attachmentName || 'file'}`
            : '📷 immagine inviata';
          const realCap = selfTxt && !['📷', '🎬', '🎙'].includes(selfTxt) && !selfTxt.startsWith('📎');
          text = label + (realCap ? ` — "${selfTxt}"` : '');
        } else {
          text = selfTxt != null ? selfTxt : '[messaggio inviato]';
        }
        return {
          key: m.id,
          mine: true,
          text,
          // I propri allegati sono riapribili SE è presente la chiave-per-sé
          // (allegati inviati dopo l'introduzione di attachmentKeySelf).
          // Riapribile dal mittente se ha la chiave-per-sé (legacy) o se è FS (v2,
          // chiave nel payload). Allegati legacy senza chiave-per-sé restano non riapribili.
          att: (m.hasAttachment && (m.attachmentKeySelfCipher || Number(m.v) === 2) && !selfRemoved) ? m : null,
          loc: !m.hasAttachment ? parseLocation(selfTxt) : null,
          createdAt: m.createdAt ?? Date.now(),
          ttl: m.senderTTL,
          viewedAt: m.viewedAt,
          canUnsend: !m.viewedAt && !selfRemoved, // unsend completo: solo finche' non letto
          canDeleteSelf: !selfRemoved,            // rimuovi solo per me: SEMPRE finche' non gia' rimosso
        };
      });
    const mineLocal = localOutgoing.map((m) => ({
      key: m.tempId,
      mine: true,
      text: m.text,
      loc: parseLocation(m.text),
      createdAt: m.createdAt,
      ttl: m.ttl,
      viewedAt: null,
    }));
    return [...theirs, ...mineRemote, ...mineLocal].sort(
      (a, b) => a.createdAt - b.createdAt
    );
  }, [remoteMsgs, decrypted, localOutgoing, myUid, myFirebaseUid, chat, nowTs]);

  const isPending = chat?.status === 'pending' || chat?.pending === true;
  // L'initiator (firebaseUid del mittente) e' chi ha aperto la richiesta;
  // chi NON e' l'initiator e' il destinatario che deve accettare.
  const initiatorUid = chat?.initiator ?? chat?.requestedBy ?? null;
  const isPendingForMe =
    isPending && !!initiatorUid &&
    initiatorUid !== myFirebaseUid && initiatorUid !== myUid;

  async function doAccept() {
    setAcceptErr(null);
    setAccepting(true);
    try {
      await acceptChatRequest(chatId, false);
      await fetchOnce();
    } catch (err) {
      console.warn('accept failed', err);
      setAcceptErr(err?.message || 'errore accept');
    } finally {
      setAccepting(false);
    }
  }

  // Determina con quale dei MIEI sigil sto operando in questa chat (master
  // o un derivato), e di conseguenza che box keys usare per cifrare.
  function getMySigilContextForChat() {
    const psigils = Array.isArray(chat?.participantsSigils) ? chat.participantsSigils : [];
    const masterLower = (myMasterSigil || '').toLowerCase();
    const otherLower = (chat?.otherSigilLower || '').toLowerCase();
    let mineLower = null;
    if (psigils.length === 2) {
      mineLower = psigils.find((s) => s !== otherLower) || null;
    }
    if (!mineLower) mineLower = masterLower;

    // Master?
    if (mineLower === masterLower) {
      return { mySigilLower: mineLower, mySigil: myMasterSigil, secKey: boxSec, pubKey: boxPub };
    }
    // Derivato?
    const d = derivedKeys?.[mineLower];
    if (d) {
      return { mySigilLower: mineLower, mySigil: d.sigil, secKey: d.boxSec, pubKey: d.boxPub };
    }
    // Fallback: master.
    return { mySigilLower: masterLower, mySigil: myMasterSigil, secKey: boxSec, pubKey: boxPub };
  }

  async function doReply() {
    setReplyErr(null);
    if (!replyText.trim()) {
      setReplyErr('messaggio vuoto');
      return;
    }
    if (replyText.length > 500) {
      setReplyErr('massimo 500 caratteri');
      return;
    }
    if (!otherUser?.pubKeyBox) {
      setReplyErr('chiave del destinatario non disponibile');
      return;
    }
    if (!boxSec) {
      setReplyErr('chiavi non inizializzate, ricarica');
      return;
    }
    const text = replyText.trim();
    const tempId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setSending(true);
    setLocalOutgoing((cur) => [
      ...cur,
      { tempId, text, ttl: replyTtl, createdAt: Date.now() },
    ]);
    setReplyText('');
    try {
      const ctx = getMySigilContextForChat();
      const recipientSigil = chat?.otherSigilLower || otherUser?.sigil || null;
      // Forward-secret di default (ratchet). Se il destinatario non ha ancora
      // pubblicato prekey (client non aggiornato), fallback al box statico così
      // il messaggio parte comunque — finestra di rollout, non retrocompat.
      try {
        await sendFsMessage({
          boxKeypair: { pub: ctx.pubKey, priv: ctx.secKey },
          recipientUid: otherUser.uid,
          recipientSigil,
          senderSigil: ctx.mySigil,
          text,
          senderTTL: replyTtl,
          myBoxPub: ctx.pubKey,
          mySecKeyBox: ctx.secKey,
        });
      } catch (e) {
        if (String(e?.message || '').includes('PEER_PREKEYS_MISSING')) {
          await sendEncryptedMessage({
            text,
            recipient: { uid: otherUser.uid, pubKeyBox: otherUser.pubKeyBox },
            mySecKeyBox: ctx.secKey,
            myBoxPub: ctx.pubKey,
            senderTTL: replyTtl,
            senderSigil: ctx.mySigil,
            recipientSigil,
          });
        } else {
          throw e;
        }
      }
      await fetchOnce();
    } catch (err) {
      console.warn('reply send error', err);
      setReplyErr(err?.message || 'errore invio');
      setLocalOutgoing((cur) => cur.filter((m) => m.tempId !== tempId));
      setReplyText(text);
    } finally {
      setSending(false);
    }
  }

  // Apre un allegato: scarta la chiave del file e mostra il viewer.
  // Se l'allegato è MIO uso la chiave-per-sé (e apro il viewer in modalità
  // self: non marca "visto", non brucia la copia del destinatario).
  async function openAttachment(att) {
    setAttachErr(null);
    const mineAtt = isMine(att);
    if (!boxSec) { setAttachErr('chiavi non pronte, ricarica'); return; }
    try {
      let key;
      if (Number(att.v) === 2) {
        // Allegato FS: la chiave-file è nel payload ratchet già decifrato.
        const k = fsFileKeysRef.current[att.id];
        if (!k) { setAttachErr('chiave non ancora pronta, riprova tra un attimo'); return; }
        key = await fromBase64Url(k);
      } else {
        if (!mineAtt && !otherUser?.pubKeyBox) { setAttachErr('chiavi non pronte, ricarica'); return; }
        key = mineAtt
          ? await unwrapAttachmentKeySelf(att, boxPub, boxSec)
          : await unwrapAttachmentKey(att, boxSec, otherUser.pubKeyBox);
      }
      setViewer({ msg: att, key, selfView: mineAtt });
    } catch (err) {
      console.warn('openAttachment failed', err);
      setAttachErr('impossibile aprire l\'allegato');
    }
  }

  // Annulla un proprio messaggio non ancora letto (cancella per entrambi).
  async function doUnsend(msgId) {
    setReplyErr(null);
    try {
      await unsendMessage(chatId, msgId);
      await fetchOnce();
    } catch (err) {
      const m = (err?.message || '').toUpperCase();
      if (m.includes('ALREADY_VIEWED')) setReplyErr('troppo tardi: già letto, non più annullabile (puoi solo "rimuoverlo dalla tua chat")');
      else { console.warn('unsend failed', err); setReplyErr('annullamento non riuscito'); }
    }
  }

  // Rimuovi un proprio messaggio SOLO dalla mia chat (anche dopo lettura).
  // La copia del destinatario resta intatta fino al suo TTL naturale.
  async function doDeleteSelf(msgId) {
    setReplyErr(null);
    try {
      await deleteSelfCopy(chatId, msgId);
      await fetchOnce();
    } catch (err) {
      console.warn('deleteSelfCopy failed', err);
      setReplyErr('rimozione locale non riuscita');
    }
  }

  // Invio core di un File come allegato: usato da file picker, paste e drop.
  // L'initiator puo' inviare anche prima dell'accept (il server permette
  // l'aggiunta di msg alla chat pending da parte di chi l'ha aperta).
  async function sendFileAttachment(file) {
    if (!file) return;
    setAttachErr(null);
    if (!otherUser?.pubKeyBox || !boxSec) {
      setAttachErr('chiavi non pronte, ricarica');
      return;
    }
    setAttaching(true);
    try {
      const ctx = getMySigilContextForChat();
      const recipientSigil = chat?.otherSigilLower || otherUser?.sigil || null;
      // Allegato forward-secret di default (chiave-file nel ratchet). Fallback al
      // box statico se il destinatario non ha ancora pubblicato prekey.
      try {
        await sendFsAttachment({
          boxKeypair: { pub: ctx.pubKey, priv: ctx.secKey },
          recipientUid: otherUser.uid,
          recipientSigil,
          senderSigil: ctx.mySigil,
          file,
          senderTTL: replyTtl,
          myBoxPub: ctx.pubKey,
          mySecKeyBox: ctx.secKey,
          downloadable: attachDownloadable,
        });
      } catch (e) {
        if (String(e?.message || '').includes('PEER_PREKEYS_MISSING')) {
          await sendEncryptedAttachment({
            file,
            recipient: { uid: otherUser.uid, pubKeyBox: otherUser.pubKeyBox },
            mySecKeyBox: ctx.secKey,
            myBoxPub: ctx.pubKey,
            senderTTL: replyTtl,
            senderSigil: ctx.mySigil,
            recipientSigil,
            downloadable: attachDownloadable,
          });
        } else { throw e; }
      }
      await fetchOnce();
    } catch (err) {
      console.error('attachment send error', err, 'file=', file);
      const raw = err?.message || String(err) || 'errore sconosciuto';
      const m = raw.toUpperCase();
      let user;
      if (m.includes('VIDEO_TOO_LONG')) user = 'Video troppo lungo (max 60s).';
      else if (m.includes('VIDEO_TOO_LARGE')) user = `Video troppo pesante (max 75MB).`;
      else if (m.includes('AUDIO_TOO_LARGE')) user = 'Audio troppo pesante (max 75MB).';
      else if (m.includes('FILE_TOO_LARGE')) user = 'File troppo pesante (max 75MB).';
      else user = 'Invio allegato fallito.';
      // Mostro SEMPRE anche l'errore raw + nome/dimensione/tipo del file,
      // cosi' chi segnala il bug ci fornisce informazioni utili.
      const sizeMB = file?.size ? (file.size / (1024 * 1024)).toFixed(2) : '?';
      setAttachErr(`${user} dettaglio: ${raw} · file=${file?.name || 'no-name'} type=${file?.type || '?'} size=${sizeMB}MB`);
    } finally {
      setAttaching(false);
    }
  }

  // Immagini → anteprima grande prima dell'invio (così vedi cosa stai mandando).
  // Altri tipi (video/audio/file) → invio diretto come prima.
  function queueOrSend(file) {
    if (!file) return;
    if (file.type && file.type.startsWith('image/')) {
      let url = null;
      try { url = URL.createObjectURL(file); } catch { url = null; }
      setPendingImg({ file, url });
    } else {
      sendFileAttachment(file);
    }
  }

  function cancelPendingImg() {
    setPendingImg((p) => { if (p?.url) { try { URL.revokeObjectURL(p.url); } catch { /* noop */ } } return null; });
  }

  async function confirmPendingImg() {
    const p = pendingImg;
    if (!p) return;
    setPendingImg(null);
    try { await sendFileAttachment(p.file); }
    finally { if (p.url) { try { URL.revokeObjectURL(p.url); } catch { /* noop */ } } }
  }

  // File picker.
  function onPickFile(e) {
    const file = e.target?.files?.[0];
    if (e.target) e.target.value = ''; // reset per riselezionare lo stesso file
    if (!file) return;
    queueOrSend(file);
  }

  // ── Condivisione posizione (una-tantum, consensuale, E2E, effimera) ─────
  async function pickLocation() {
    setAttachErr(null);
    setLocBusy(true);
    try {
      const pos = await getCurrentPositionOnce();
      setLocPrecision('exact');
      setPendingLoc(pos);
    } catch (e) {
      const code = e?.code;
      let msg = 'Impossibile ottenere la posizione.';
      if (code === 1 || /denied|permission/i.test(String(e?.message))) {
        msg = 'Permesso posizione negato. Abilitalo nelle impostazioni del browser per condividere.';
      } else if (code === 2) msg = 'Posizione non disponibile (segnale assente).';
      else if (code === 3) msg = 'Timeout: riprova dove c\'è segnale migliore.';
      else if (/GEO_UNSUPPORTED/.test(String(e?.message))) msg = 'Questo browser non supporta la geolocalizzazione.';
      setAttachErr(msg);
    } finally {
      setLocBusy(false);
    }
  }

  function cancelPendingLoc() { setPendingLoc(null); }

  async function confirmSendLocation() {
    const p = pendingLoc;
    if (!p) return;
    if (!otherUser?.pubKeyBox || !boxSec) { setAttachErr('chiavi non pronte, ricarica'); return; }
    const payload = buildLocationPayload({ lat: p.lat, lng: p.lng, acc: p.acc, precision: locPrecision });
    const tempId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setPendingLoc(null);
    setLocBusy(true);
    setLocalOutgoing((cur) => [...cur, { tempId, text: payload, ttl: replyTtl, createdAt: Date.now() }]);
    try {
      const ctx = getMySigilContextForChat();
      const recipientSigil = chat?.otherSigilLower || otherUser?.sigil || null;
      try {
        await sendFsMessage({
          boxKeypair: { pub: ctx.pubKey, priv: ctx.secKey },
          recipientUid: otherUser.uid,
          recipientSigil,
          senderSigil: ctx.mySigil,
          text: payload,
          senderTTL: replyTtl,
          myBoxPub: ctx.pubKey,
          mySecKeyBox: ctx.secKey,
        });
      } catch (e) {
        if (String(e?.message || '').includes('PEER_PREKEYS_MISSING')) {
          await sendEncryptedMessage({
            text: payload,
            recipient: { uid: otherUser.uid, pubKeyBox: otherUser.pubKeyBox },
            mySecKeyBox: ctx.secKey,
            myBoxPub: ctx.pubKey,
            senderTTL: replyTtl,
            senderSigil: ctx.mySigil,
            recipientSigil,
          });
        } else { throw e; }
      }
      await fetchOnce();
    } catch (err) {
      console.warn('location send error', err);
      setAttachErr(err?.message || 'invio posizione fallito');
      setLocalOutgoing((cur) => cur.filter((m) => m.tempId !== tempId));
    } finally {
      setLocBusy(false);
    }
  }

  // Paste di immagini/file direttamente nel textarea (Ctrl/Cmd+V).
  // Non interrompiamo il paste del testo: prendiamo solo gli item file.
  async function onPasteReply(e) {
    const items = e.clipboardData?.items;
    if (!items || items.length === 0) return;
    const files = [];
    for (const it of items) {
      if (it.kind === 'file') {
        const f = it.getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length === 0) return; // solo testo: lascia il paste nativo
    e.preventDefault();
    // Singola immagine incollata (es. screenshot) → anteprima prima di inviare.
    if (files.length === 1 && files[0].type?.startsWith('image/')) {
      const f0 = files[0];
      const named0 = f0.name ? f0 : new File([f0], `incollato-${Date.now()}.${(f0.type.split('/')[1] || 'png')}`, { type: f0.type });
      queueOrSend(named0);
      return;
    }
    for (const f of files) {
      // Se l'item incollato e' un image/* generico (es. screenshot da
      // clipboard) il name puo' mancare: lo battezziamo cosi' il viewer
      // mostra qualcosa di sensato come fallback.
      const named = f.name ? f : new File([f], `incollato-${Date.now()}.${(f.type.split('/')[1] || 'bin')}`, { type: f.type });
      // eslint-disable-next-line no-await-in-loop
      await sendFileAttachment(named);
    }
  }

  // Drag&drop sul textarea (desktop): stesso flusso del paste.
  async function onDropReply(e) {
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length === 0) return;
    e.preventDefault();
    for (const f of files) {
      // eslint-disable-next-line no-await-in-loop
      await sendFileAttachment(f);
    }
  }

  if (chatErr) {
    return (
      <Frame statusLeft="CHAT :: ERROR" statusRight="5G">
        <Header
          title="// CHAT"
          crumb="◀ inbox"
          onCrumbClick={() => navigate('/inbox')}
        />
        <Box variant="warn" className="text-[16px]">
          <p className="break-words">{chatErr}</p>
        </Box>
        <Footer>
          <Btn onClick={() => navigate('/inbox')}>◀ Inbox</Btn>
        </Footer>
      </Frame>
    );
  }

  return (
    <Frame
      statusLeft={`CHAT :: @${otherUser?.sigil || '...'}`}
      statusRight="5G"
    >
      <Header
        title={`// @${otherUser?.sigil || '...'}`}
        crumb="◀ inbox"
        onCrumbClick={() => navigate('/inbox')}
      />

      {keyChanged && (
        <Box variant="danger" className="mb-3 text-[14px]">
          <p className="text-red-500 mb-1">⚠ La chiave di sicurezza di @{otherUser?.sigil || '???'} è cambiata.</p>
          <p className="text-ink-dim">
            non dovrebbe mai succedere. potrebbe essere un tentativo di intercettazione,
            oppure @{otherUser?.sigil} ha ricreato la sua identità. verificate di persona
            prima di scrivere cose sensibili.
          </p>
        </Box>
      )}

      {safetyCode && (
        <div className="mb-3 text-[12px]">
          <button
            onClick={() => setShowSafety((v) => !v)}
            className="text-ink-dim hover:text-ink"
          >
            🔒 codice di sicurezza {showSafety ? '▴' : '▾'}
          </button>
          {showSafety && (
            <Box className="mt-1 text-[13px]">
              <div className="text-ink text-glow tracking-[0.25em] text-[16px] mb-1">{safetyCode}</div>
              <p className="text-ink-dim">
                leggi questo codice a voce a @{otherUser?.sigil || '???'}: se combacia con
                il suo, nessuno sta intercettando la conversazione.
              </p>
            </Box>
          )}
          <button
            onClick={toggleReadReceipts}
            className="text-ink-dim hover:text-ink ml-3"
            title="mostra/nascondi 'letto' e countdown di scomparsa sui tuoi messaggi"
          >
            {readReceipts ? '✓✓ conferme di lettura: ON' : '✓✓ conferme di lettura: OFF'}
          </button>
        </div>
      )}

      {isPending && isPendingForMe && (
        <Box variant="warn" className="mb-3 text-[16px]">
          <p className="text-amber mb-2">
            ⚠ @{otherUser?.sigil || '???'} ti ha inviato una richiesta di chat
          </p>
          <p className="text-ink-dim mb-1">
            🔒 {remoteMsgs.filter((m) => !isMine(m)).length || 0}
            {' '}messagg{remoteMsgs.filter((m) => !isMine(m)).length === 1 ? 'io' : 'i'}
            {' '}in attesa, non li abbiamo ancora decifrati sul tuo device.
          </p>
          <p className="text-ink-dim mb-2 text-[14px]">
            accetta per leggerli e poter rispondere. se rifiuti, basta non
            accettare: nessuno saprà che hai aperto la chat.
          </p>
          {acceptErr && (
            <p className="text-red-500 text-[13px] mb-2">{acceptErr}</p>
          )}
          <Btn onClick={doAccept} disabled={accepting}>
            {accepting ? '...' : '✓ Accetta e leggi'}
          </Btn>
        </Box>
      )}

      {isPending && !isPendingForMe && (
        <Box variant="warn" className="mb-3 text-[15px]">
          <p className="text-amber mb-1">
            ⌛ Richiesta inviata — in attesa che @{otherUser?.sigil || '???'} ti confermi
          </p>
          <p className="text-ink-dim">
            Hai completato l&apos;accesso. Puoi già scrivere: i tuoi messaggi gli arriveranno
            e potrà risponderti dopo che ti conferma come contatto.
          </p>
        </Box>
      )}

      <div className="flex-1 overflow-y-auto -mx-2 px-2 space-y-2">
        {renderedMsgs.length === 0 && (
          <div className="text-[16px] text-ink-dim text-center py-6">
            nessun messaggio ancora
          </div>
        )}
        {renderedMsgs.map((m) => (
          <div
            key={m.key}
            className={`flex ${m.mine ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] border px-3 py-2 text-[15px] rounded-2xl ${
                m.mine
                  ? 'border-ink/60 text-ink bg-ink/[0.05] rounded-br-md'
                  : 'border-amber/60 text-amber bg-amber/[0.05] rounded-bl-md'
              }`}
            >
              {m.att && (
                <button
                  onClick={() => openAttachment(m.att)}
                  className="block w-full text-left border border-amber/60 rounded-lg text-amber px-2 py-2 mb-1 hover:bg-amber/10"
                >
                  {(() => {
                    const once = m.att.downloadable ? '(salvabile)' : '(una volta)';
                    return m.att.kind === 'video'
                      ? `🎬 video — tocca per vedere ${once}`
                      : m.att.kind === 'audio'
                      ? `🎙 audio — tocca per ascoltare ${once}`
                      : m.att.kind === 'file'
                      ? `📎 ${m.att.attachmentName || 'file'} — tocca per scaricare ${once}`
                      : `📷 immagine — tocca per aprire ${once}`;
                  })()}
                </button>
              )}
              {m.loc ? (
                <div className="border border-ink-dim/50 rounded-lg p-2 my-1 text-[13px]">
                  <div className="text-ink">📍 Posizione condivisa{m.loc.precision === 'approx' ? ' (approssimativa)' : ''}</div>
                  <div className="text-ink-dim text-[12px] font-mono select-all break-all">
                    {m.loc.lat}, {m.loc.lng}{m.loc.acc != null ? ` · ±${m.loc.acc} m` : ''}
                  </div>
                  <a
                    href={osmLink(m.loc.lat, m.loc.lng)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-ink underline text-[12px]"
                  >
                    Apri nelle mappe ↗
                  </a>
                </div>
              ) : (
                <div className="whitespace-pre-wrap break-words">{m.text}</div>
              )}
              <div className="text-[11px] text-ink-dim mt-1 flex justify-between gap-3 items-center">
                <span className="flex items-center gap-2">
                  {fmtClock(m.createdAt)}
                  {m.mine && readReceipts && (() => {
                    // Indicatore di lettura + countdown di scomparsa (solo miei msg).
                    if (!m.viewedAt) return <span className="text-ink-dim">· → non letto</span>;
                    if (m.ttl === -1) return <span className="text-green-400">· ✓✓ letto</span>;
                    if (m.ttl === 0) return <span className="text-green-400">· ✓✓ letto · sparisce</span>;
                    const rem = Math.ceil((m.viewedAt + (m.ttl || 0) * 1000 - nowTs) / 1000);
                    return rem > 0
                      ? <span className="text-green-400">· ✓✓ letto · sparisce tra {rem}s</span>
                      : <span className="text-ink-dim">· ✓✓ letto · in cancellazione…</span>;
                  })()}
                </span>
                <span className="flex items-center gap-2">
                  {m.ttl !== undefined && m.ttl !== null && (
                    <span>
                      TTL:{' '}
                      {m.ttl === 0
                        ? 'zero-persist'
                        : m.ttl === -1
                        ? '∞'
                        : m.ttl === 86400
                        ? '24h'
                        : m.ttl >= 3600
                        ? `${Math.round(m.ttl / 3600)}h`
                        : m.ttl >= 60
                        ? `${Math.round(m.ttl / 60)}m`
                        : `${m.ttl}s`}
                    </span>
                  )}
                  {m.canUnsend && (
                    <button
                      onClick={() => doUnsend(m.key)}
                      title="annulla anche per il destinatario (solo se non ancora letto)"
                      className="text-red-400 hover:text-red-300 border border-red-500/40 px-1.5 rounded"
                    >
                      ✕ annulla
                    </button>
                  )}
                  {m.mine && m.canDeleteSelf && (
                    <button
                      onClick={() => doDeleteSelf(m.key)}
                      title="rimuove solo dalla tua chat (la copia del destinatario resta)"
                      className="text-amber hover:text-amber/70 border border-amber/40 px-1.5 rounded"
                    >
                      − solo per me
                    </button>
                  )}
                </span>
              </div>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {!isPendingForMe && (
        <div className="mt-3 space-y-2 border-t border-ink-dim/30 pt-2">
          {attachMenuOpen && ATTACHMENTS_ENABLED && (
            <div className="border border-ink-dim/50 rounded-xl bg-ink/[0.015] p-3 text-[14px]">
              <button
                onClick={() => setAttachDownloadable((v) => !v)}
                className={`w-full mb-2 px-3 py-2 border rounded-lg text-left ${attachDownloadable ? 'border-emerald-500 text-emerald-400' : 'border-ink-dim/60 text-ink-dim'}`}
              >
                {attachDownloadable ? '📥 Scaricabile dal destinatario: ON' : '👁 Solo visione (effimero): ON'}
                <div className="text-[11px] text-ink-dim mt-0.5">
                  {attachDownloadable
                    ? 'il destinatario potrà salvare una copia, ma l\'allegato sparisce comunque dopo il TTL scelto'
                    : 'one-view: si brucia dopo la visione, niente salvataggio'}
                </div>
              </button>
              {attachDownloadable && (
                <div className="mb-2 flex items-center gap-2 text-[12px] text-ink-dim">
                  <span>Durata (TTL):</span>
                  <select
                    value={replyTtl}
                    onChange={(e) => setReplyTtl(Number(e.target.value))}
                    className="bg-black border border-ink-dim rounded-lg text-ink font-mono text-[13px] px-2 py-1 focus:border-ink focus:outline-none"
                  >
                    {TTL_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => { setAttachMenuOpen(false); inputGalleryRef.current?.click(); }}
                className="border border-ink-dim/60 rounded-lg text-ink px-2.5 py-2 hover:border-ink text-left"
              >
                🖼 Foto / video da galleria
              </button>
              <button
                onClick={() => { setAttachMenuOpen(false); inputVideoLiveRef.current?.click(); }}
                className="border border-ink-dim/60 rounded-lg text-ink px-2.5 py-2 hover:border-ink text-left"
              >
                🎥 Registra video ora
              </button>
              <button
                onClick={() => { setAttachMenuOpen(false); inputAudioLiveRef.current?.click(); }}
                className="border border-ink-dim/60 rounded-lg text-ink px-2.5 py-2 hover:border-ink text-left"
              >
                🎙 Registra audio
              </button>
              <button
                onClick={() => { setAttachMenuOpen(false); inputAnyFileRef.current?.click(); }}
                className="border border-ink-dim/60 rounded-lg text-ink px-2.5 py-2 hover:border-ink text-left"
              >
                📁 File qualsiasi
              </button>
              <button
                onClick={() => { setAttachMenuOpen(false); pickLocation(); }}
                disabled={locBusy}
                className="col-span-2 border border-ink-dim/60 rounded-lg text-ink px-2.5 py-2 hover:border-ink text-left disabled:opacity-40"
              >
                📍 {locBusy ? 'localizzo…' : 'Posizione attuale'}
              </button>
              <button
                onClick={() => setAttachMenuOpen(false)}
                className="col-span-2 text-[12px] text-ink-dim hover:text-ink py-1"
              >
                annulla
              </button>
              </div>
            </div>
          )}
          {pendingImg && (
            <div
              className="fixed inset-0 z-50 bg-black/95 flex flex-col items-center justify-center p-4"
              onClick={cancelPendingImg}
            >
              <div className="max-w-3xl w-full flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
                <p className="text-ink text-[14px] self-start">🖼 Anteprima — controlla prima di inviare</p>
                {pendingImg.url ? (
                  <img
                    src={pendingImg.url}
                    alt="anteprima"
                    className="max-h-[70vh] w-auto max-w-full object-contain border border-ink-dim"
                  />
                ) : (
                  <p className="text-ink-dim text-[14px]">{pendingImg.file?.name || 'immagine'}</p>
                )}
                <p className="text-[12px] text-ink-dim self-start">
                  durata: {TTL_OPTIONS.find((o) => o.value === replyTtl)?.label || replyTtl}
                  {' · '}{attachDownloadable ? 'salvabile dal destinatario' : 'solo visione (una volta)'}
                </p>
                {attachErr && <p className="text-[13px] text-red-400 self-start break-words">{attachErr}</p>}
                <div className="flex gap-3 w-full">
                  <Btn variant="primary" className="flex-1" onClick={confirmPendingImg} disabled={attaching}>
                    {attaching ? '… invio' : '✓ Invia'}
                  </Btn>
                  <Btn variant="ghost" className="flex-1" onClick={cancelPendingImg} disabled={attaching}>
                    Annulla
                  </Btn>
                </div>
              </div>
            </div>
          )}
          {pendingLoc && (
            <div
              className="fixed inset-0 z-50 bg-black/95 flex flex-col items-center justify-center p-4"
              onClick={cancelPendingLoc}
            >
              <div className="max-w-md w-full flex flex-col gap-3 border border-ink-dim p-4 bg-black" onClick={(e) => e.stopPropagation()}>
                <p className="text-ink text-[15px]">📍 Condividi la tua posizione</p>
                <div className="text-[12px] text-ink-dim font-mono select-all">
                  {locPrecision === 'approx' ? (Math.round(pendingLoc.lat * 100) / 100) : pendingLoc.lat.toFixed(5)}
                  {', '}
                  {locPrecision === 'approx' ? (Math.round(pendingLoc.lng * 100) / 100) : pendingLoc.lng.toFixed(5)}
                  {locPrecision === 'exact' && pendingLoc.acc != null ? ` · ±${Math.round(pendingLoc.acc)} m` : ''}
                </div>
                <div>
                  <div className="text-[12px] text-ink-dim mb-1">precisione (scegli tu)</div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setLocPrecision('exact')}
                      className={`flex-1 border px-2 py-2 text-[13px] ${locPrecision === 'exact' ? 'border-ink text-ink' : 'border-ink-dim text-ink-dim hover:border-ink'}`}
                    >
                      🎯 Esatta
                    </button>
                    <button
                      onClick={() => setLocPrecision('approx')}
                      className={`flex-1 border px-2 py-2 text-[13px] ${locPrecision === 'approx' ? 'border-ink text-ink' : 'border-ink-dim text-ink-dim hover:border-ink'}`}
                    >
                      🌫 Approssimativa (~1 km)
                    </button>
                  </div>
                </div>
                <p className="text-[12px] text-ink-dim">
                  durata: {TTL_OPTIONS.find((o) => o.value === replyTtl)?.label || replyTtl}
                  {' · '}cifrata end-to-end, sparisce col TTL. Il server non la vede.
                </p>
                {attachErr && <p className="text-[13px] text-red-400 break-words">{attachErr}</p>}
                <div className="flex gap-3">
                  <Btn variant="primary" className="flex-1" onClick={confirmSendLocation} disabled={locBusy}>
                    {locBusy ? '… invio' : '✓ Invia posizione'}
                  </Btn>
                  <Btn variant="ghost" className="flex-1" onClick={cancelPendingLoc} disabled={locBusy}>Annulla</Btn>
                </div>
              </div>
            </div>
          )}
          {/* Durata del messaggio: compatta, non sempre a vista. */}
          <div className="flex items-center gap-2 text-[12px] text-ink-dim">
            <button
              onClick={() => setShowTtl((v) => !v)}
              className="border border-ink-dim px-2 py-1 hover:border-ink shrink-0"
            >
              ⏱ {TTL_OPTIONS.find((o) => o.value === replyTtl)?.label || replyTtl} {showTtl ? '▾' : '▸'}
            </button>
            <span>durata dopo la lettura</span>
          </div>
          {showTtl && (
            <select
              value={replyTtl}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (v === 86400) {
                  alert(
                    'Durata 24 ore.\n\n'
                    + 'Il messaggio resta leggibile dal destinatario per 24h dalla lettura, '
                    + 'poi sparisce.\n\n'
                    + 'Puoi annullarlo solo finche\' il destinatario NON l\'ha ancora aperto: '
                    + 'dopo la prima lettura, l\'unsend non e\' piu\' disponibile (solo "rimuovi '
                    + 'dalla mia chat" lato tuo).'
                  );
                } else if (v === -1) {
                  alert(
                    'Durata ∞ (Premium).\n\n'
                    + 'Il messaggio NON viene mai cancellato automaticamente. '
                    + 'Diventa simile a una chat tradizionale.\n\n'
                    + 'Puoi annullarlo solo finche\' il destinatario NON l\'ha ancora letto. '
                    + 'Dopo la lettura puoi solo "rimuoverlo dalla tua chat" senza toccare la copia del destinatario.'
                  );
                }
                setReplyTtl(v);
              }}
              className="w-full bg-black border border-ink-dim rounded-lg text-ink font-mono text-[14px] px-3 py-2 focus:border-ink focus:outline-none"
            >
              {TTL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          )}
          <div className="flex gap-2 items-end">
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onPaste={onPasteReply}
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDropReply}
              maxLength={500}
              rows={2}
              placeholder="Scrivi un messaggio…"
              className="flex-1 bg-black border border-ink-dim rounded-lg text-ink font-mono text-[15px] px-3 py-2 focus:border-ink focus:outline-none resize-none"
            />
            {ATTACHMENTS_ENABLED && (
              <>
                {/* 4 input nascosti: ogni bottone del menu attiva quello giusto */}
                <input ref={inputGalleryRef} type="file" accept="image/*,video/*"
                  className="hidden" onChange={onPickFile} />
                <input ref={inputVideoLiveRef} type="file" accept="video/*"
                  capture="environment" className="hidden" onChange={onPickFile} />
                <input ref={inputAudioLiveRef} type="file" accept="audio/*"
                  capture className="hidden" onChange={onPickFile} />
                <input ref={inputAnyFileRef} type="file"
                  className="hidden" onChange={onPickFile} />
                <button
                  onClick={() => setAttachMenuOpen((v) => !v)}
                  disabled={attaching}
                  title="allega foto, video, audio, file o posizione"
                  className="border border-ink-dim/60 rounded-lg text-ink px-3 py-2 text-[16px] hover:border-ink disabled:opacity-40 shrink-0"
                >
                  {attaching ? '…' : '📎'}
                </button>
              </>
            )}
            <button
              onClick={doReply}
              disabled={sending || !replyText.trim()}
              className="border border-ink rounded-lg bg-ink/10 text-ink px-4 py-2 text-[16px] hover:bg-ink/20 transition-colors disabled:opacity-40 shrink-0"
            >
              {sending ? '...' : '▶'}
            </button>
          </div>
          {replyErr && (
            <p className="text-red-500 text-[13px]">{replyErr}</p>
          )}
          {attachErr && (
            <p className="text-red-500 text-[13px]">{attachErr}</p>
          )}
          <p className="text-[12px] text-ink-dim">
            {replyText.length}/500 · il timer di scomparsa parte quando l'altro apre il messaggio
          </p>
        </div>
      )}

      <Footer>
        <Btn variant="ghost" onClick={() => navigate('/inbox')}>
          ◀ Inbox
        </Btn>
      </Footer>

      {viewer && (
        <AttachmentViewer
          message={viewer.msg}
          chatId={chatId}
          attachmentKey={viewer.key}
          selfView={viewer.selfView}
          onClose={() => { setViewer(null); fetchOnce(); }}
        />
      )}
    </Frame>
  );
}
